import type { part } from "@/api/supplierService";
import {
  Box,
  Checkbox,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { FileOpen } from "@mui/icons-material";
import FlagIcon from "@mui/icons-material/Flag";
import { Link as RouterLink } from "react-router-dom";

interface PartManagementTableProps {
  rows: part[];
  rowCount: number;
  isLoading: boolean;
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  selectedParts: part[];
  setSelectedParts: React.Dispatch<React.SetStateAction<part[]>>;
  getDetailUrl: (partId: string | null) => string | null;
  handleFlagPart: (partInfo: part) => () => void;
  canEdit: boolean;
}

const PartManagementTable = ({
  rows,
  rowCount,
  isLoading,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  selectedParts,
  setSelectedParts,
  getDetailUrl,
  handleFlagPart,
  canEdit,
}: PartManagementTableProps) => {
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  useEffect(() => {
    if (
      rows.every((part) =>
        selectedParts.some((row) => row.part_id === part.part_id),
      ) &&
      rows.length > 0
    ) {
      setIsAllSelected(true);
    } else {
      setIsAllSelected(false);
    }
  }, [selectedParts, rows]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleSelect = (part: part, checked: boolean) => {
    if (checked) {
      setSelectedParts((prev: part[]) => [...prev, part]);
    } else {
      setSelectedParts((prev: part[]) =>
        prev.filter((p) => p.part_id !== part.part_id),
      );
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedParts((prev) => [
        ...prev,
        ...rows.filter((part) => !prev.some((p) => p.part_id === part.part_id)),
      ]);
    } else {
      setSelectedParts([]);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        mt: 1,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "70vh",
          overflow: "auto",
          position: "relative",
          width: "100%",
          backgroundColor: "#f3f3f3ff",
        }}
      >
        <Table stickyHeader sx={{ tableLayout: "fixed", width: "auto" }}>
          <TableHead>
            <TableRow>
              <TableCell
                rowSpan={2}
                sx={{
                  position: "sticky",
                  left: 0,
                  top: 0,
                  zIndex: 103,
                  width: 70,
                  minWidth: 70,
                  maxWidth: 70,
                  textAlign: "center",
                  borderRight: "1px solid #e0e0e0",
                }}
              >
                <Checkbox
                  checked={isAllSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  color="warning"
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: "white",
                    },
                  }}
                />
              </TableCell>
              <TableCell
                rowSpan={2}
                sx={{
                  position: "sticky",
                  left: 70,
                  top: 0,
                  zIndex: 103,
                  width: 120,
                  minWidth: 120,
                  maxWidth: 120,
                  textAlign: "center",
                  borderRight: "1px solid #e0e0e0",
                }}
              >
                Action
              </TableCell>
              <TableCell
                rowSpan={2}
                sx={{
                  position: "sticky",
                  left: 190,
                  top: 0,
                  zIndex: 103,
                  width: 150,
                  minWidth: 150,
                  maxWidth: 150,
                  textAlign: "left",
                  borderRight: "2px solid #e0e0e0",
                }}
              >
                Part No.
              </TableCell>
              <TableCell
                rowSpan={2}
                sx={{
                  position: "sticky",
                  left: 340,
                  top: 0,
                  zIndex: 103,
                  width: 200,
                  minWidth: 200,
                  maxWidth: 200,
                  textAlign: "left",
                  borderRight: "2px solid #e0e0e0",
                }}
              >
                Part Name
              </TableCell>
              <TableCell
                rowSpan={2}
                sx={{
                  position: "sticky",
                  left: 540,
                  top: 0,
                  zIndex: 103,
                  width: 70,
                  minWidth: 70,
                  maxWidth: 70,
                  textAlign: "center",
                  borderRight: "2px solid #e0e0e0",
                }}
              >
                AC
              </TableCell>
              <TableCell
                rowSpan={2}
                sx={{
                  position: "sticky",
                  left: 610,
                  top: 0,
                  zIndex: 103,
                  width: 90,
                  minWidth: 90,
                  maxWidth: 90,
                  textAlign: "center",
                  borderRight: "2px solid #e0e0e0",
                }}
              >
                Routing
              </TableCell>
              <TableCell
                colSpan={3}
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 102,
                  minWidth: 450,
                  textAlign: "center",
                  borderRight: "2px solid #e0e0e0",
                  borderLeft: "1px solid #e0e0e0",
                  fontWeight: 600,
                }}
              >
                OEM Price (THB)
              </TableCell>
              <TableCell
                colSpan={3}
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 102,
                  minWidth: 450,
                  textAlign: "center",
                  borderRight: "1px solid #e0e0e0",
                  fontWeight: 600,
                }}
              >
                Spare Price (THB)
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                sx={{
                  position: "sticky",
                  top: 53.8,
                  zIndex: 102,
                  minWidth: 150,
                  maxWidth: 150,
                  textAlign: "center",
                  borderRight: "1px solid #e0e0e0",
                  borderLeft: "1px solid #e0e0e0",
                }}
              >
                Current Price
              </TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  top: 53.8,
                  zIndex: 102,
                  minWidth: 150,
                  maxWidth: 150,
                  textAlign: "center",
                  borderRight: "1px solid #e0e0e0",
                }}
              >
                New Price
              </TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  top: 53.8,
                  zIndex: 102,
                  minWidth: 150,
                  maxWidth: 150,
                  textAlign: "center",
                  borderRight: "2px solid #e0e0e0",
                }}
              >
                Diff
              </TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  top: 53.8,
                  zIndex: 102,
                  minWidth: 150,
                  maxWidth: 150,
                  textAlign: "center",
                  borderRight: "1px solid #e0e0e0",
                }}
              >
                Current Price
              </TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  top: 53.8,
                  zIndex: 102,
                  minWidth: 150,
                  maxWidth: 150,
                  textAlign: "center",
                  borderRight: "1px solid #e0e0e0",
                }}
              >
                New Price
              </TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  top: 53.8,
                  zIndex: 102,
                  minWidth: 150,
                  maxWidth: 150,
                  textAlign: "center",
                  borderRight: "1px solid #e0e0e0",
                }}
              >
                Diff
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={13}>
                  <Skeleton variant="text" height={40} animation="wave" />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((part) => (
                <TableRow
                  key={part.part_id}
                  sx={{
                    backgroundColor:
                      (!part.oem_price.current_price &&
                        !part.spare_price.current_price &&
                        !part.oem_price.new_price &&
                        !part.spare_price.new_price) ||
                      part.has_price_in_v2
                        ? "#d3d3d3"
                        : "white",
                  }}
                >
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 101,
                      width: 70,
                      minWidth: 70,
                      maxWidth: 70,
                      textAlign: "center",
                      backgroundColor: "inherit",
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    <Box display="flex" gap={1}>
                      <Checkbox
                        onChange={(e) => {
                          handleSelect(part, e.target.checked);
                        }}
                        checked={selectedParts.some(
                          (p) => p.part_id === part.part_id,
                        )}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 70,
                      zIndex: 101,
                      width: 120,
                      minWidth: 120,
                      maxWidth: 120,
                      textAlign: "center",
                      backgroundColor: "inherit",
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    <Tooltip
                      title={"Flag"}
                      placement="top"
                      slotProps={{
                        popper: {
                          modifiers: [
                            { name: "offset", options: { offset: [0, -10] } },
                          ],
                        },
                      }}
                    >
                      <IconButton
                        disabled={!canEdit}
                        onClick={handleFlagPart(part)}
                      >
                        <FlagIcon
                          sx={{
                            color: part.is_flagged
                              ? part.is_admin_flagged
                                ? "success.light"
                                : "error.main"
                              : "action.disabled",
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={"รายละเอียด"}
                      placement="top"
                      slotProps={{
                        popper: {
                          modifiers: [
                            { name: "offset", options: { offset: [0, -10] } },
                          ],
                        },
                      }}
                    >
                      <IconButton
                        component={RouterLink}
                        to={
                          getDetailUrl(part?.part_id?.toString() ?? null) ?? ""
                        }
                        disabled={part.is_active === false}
                      >
                        <FileOpen
                          color={part.is_active ? "primary" : "disabled"}
                        />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 190,
                      zIndex: 101,
                      width: 150,
                      minWidth: 150,
                      maxWidth: 150,
                      textAlign: "left",
                      backgroundColor: "inherit",
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    {part.part_number}
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 340,
                      zIndex: 101,
                      width: 200,
                      minWidth: 200,
                      maxWidth: 200,
                      textAlign: "left",
                      backgroundColor: "inherit",
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    {part.part_name}
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 540,
                      zIndex: 101,
                      width: 70,
                      minWidth: 70,
                      maxWidth: 70,
                      textAlign: "center",
                      backgroundColor: "inherit",
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    {part.ac}
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 610,
                      zIndex: 101,
                      width: 90,
                      minWidth: 90,
                      maxWidth: 90,
                      textAlign: "center",
                      backgroundColor: "inherit",
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    }}
                  >
                    {part.rt}
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 150,
                      maxWidth: 150,
                      textAlign: "right",
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                      fontSize: "0.9rem",
                    }}
                  >
                    {part.oem_price.current_price?.toLocaleString() || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 150,
                      maxWidth: 150,
                      textAlign: "right",
                      borderRight: "1px solid #e0e0e0",
                      fontSize: "0.9rem",
                    }}
                  >
                    {part.oem_price.new_price?.toLocaleString() || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 150,
                      maxWidth: 150,
                      textAlign: "right",
                      borderRight: "2px solid #e0e0e0",
                      fontSize: "0.9rem",
                      color:
                        part.oem_price.new_price -
                          part.oem_price.current_price >
                        0
                          ? "red"
                          : part.oem_price.new_price -
                                part.oem_price.current_price <
                              0
                            ? "green"
                            : "black",
                      fontWeight: 500,
                    }}
                  >
                    {(
                      part.oem_price.new_price - part.oem_price.current_price
                    )?.toLocaleString() || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 150,
                      maxWidth: 150,
                      textAlign: "right",
                      borderRight: "1px solid #e0e0e0",
                      fontSize: "0.9rem",
                    }}
                  >
                    {part.spare_price.current_price?.toLocaleString() || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 150,
                      maxWidth: 150,
                      textAlign: "right",
                      borderRight: "1px solid #e0e0e0",
                      fontSize: "0.9rem",
                    }}
                  >
                    {part.spare_price.new_price?.toLocaleString() || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 150,
                      maxWidth: 150,
                      textAlign: "right",
                      borderRight: "2px solid #e0e0e0",
                      fontSize: "0.9rem",
                      color:
                        part.oem_price.new_price -
                          part.oem_price.current_price >
                        0
                          ? "red"
                          : part.oem_price.new_price -
                                part.oem_price.current_price <
                              0
                            ? "green"
                            : "black",
                      fontWeight: 500,
                    }}
                  >
                    {(
                      part.spare_price.new_price -
                      part.spare_price.current_price
                    )?.toLocaleString() || "-"}
                  </TableCell>
                </TableRow>
              ))
            )}
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={13}
                  align="center"
                  sx={{ height: 150, verticalAlign: "middle", fontWeight: 500 }}
                >
                  ไม่มีข้อมูล
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={rowCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />
    </Box>
  );
};

export default PartManagementTable;
